import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, X, Smartphone, Hand, Info, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CameraTipsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProceed: (fingerSize: "small" | "medium" | "large") => void;
  initialFingerSize?: "small" | "medium" | "large";
}

export const CameraTipsDialog = ({
 
  open, 
  onOpenChange, 
  onProceed,
  initialFingerSize = "medium"
}: CameraTipsDialogProps) => {
  const { t } = useTranslation();
  const [selectedSize, setSelectedSize] = useState<"small" | "medium" | "large">(initialFingerSize);

  useEffect(() => {
    if (initialFingerSize) {
      setSelectedSize(initialFingerSize);
    }
  }, [initialFingerSize]);

  const handleProceed = () => {
    onProceed(selectedSize);
  };

  const handleDontShowAgain = () => {
    localStorage.setItem("camera_tips_dismissed", "true");
    onProceed(selectedSize);
  };

  const sizes = [
    { id: "small", label: "Small", range: "15-17mm", icon: "👌" },
    { id: "medium", label: "Medium", range: "18-20mm", icon: "👆" },
    { id: "large", label: "Large", range: "21-23mm", icon: "☝️" },
  ] as const;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] p-0 gap-0 overflow-hidden border-0 sm:border sm:rounded-2xl h-[100dvh] sm:h-[85vh] flex flex-col bg-background [&>button]:hidden">
        
        {/* Hero Section with Gradient */}
        <div className="relative bg-gradient-to-b from-blue-600 to-blue-700 text-white p-6 pt-10 flex flex-col items-center text-center shrink-0 overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" />
            
            <Button 
                variant="ghost" 
                size="icon" 
                className="absolute right-4 top-4 text-white/70 hover:text-white hover:bg-white/10 rounded-full"
                onClick={() => onOpenChange(false)}
            >
                <X className="h-6 w-6" />
            </Button>
            
            <div className="mb-4 relative z-10 w-full max-w-[300px] h-[200px] mx-auto">
                {/* Scene Container */}
                <div className="relative w-full h-full">
                    
                    {/* Plate (Bottom Right) */}
                    <div className="absolute bottom-0 right-8 w-32 h-32 perspective-1000">
                        <div 
                            className="w-full h-full bg-white/5 rounded-full border-2 border-white/20 flex items-center justify-center shadow-xl relative"
                            style={{ transform: "rotateX(65deg)", transformStyle: "preserve-3d" }}
                        >
                            {/* Food representation: Apple */}
                            <div 
                                className="relative"
                                style={{ transform: "rotateX(-65deg) translateY(-15px) translateZ(20px)" }}
                            >
                                {/* Apple Body */}
                                <div className="w-14 h-14 bg-white rounded-full shadow-2xl relative flex items-center justify-center">
                                    {/* Leaf */}
                                    <div className="absolute -top-3 left-1/2 w-6 h-3 bg-white rounded-full rounded-bl-none -rotate-45 origin-bottom-left" />
                                    {/* Stem */}
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-1 h-3 bg-white/80 rounded-full" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Phone (Top Left) */}
                    <motion.div 
                        className="absolute top-8 left-12 origin-bottom-right"
                        initial={{ rotate: 0, x: 0, y: 0 }}
                        animate={{ rotate: 30, x: -12, y: -12 }}
                        transition={{ 
                            duration: 2, 
                            repeat: Infinity, 
                            repeatType: "reverse", 
                            ease: "easeInOut",
                            repeatDelay: 0.5
                        }}
                    >
                        {/* Phone Body */}
                        <div className="relative z-20 bg-slate-900 border-2 border-slate-700 w-16 h-28 rounded-2xl shadow-2xl flex flex-col items-center p-2">
                            {/* Screen */}
                            <div className="w-full h-full bg-slate-800 rounded-xl overflow-hidden relative">
                                {/* Camera UI */}
                                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full border-2 border-white/30" />
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20" />
                            </div>
                            {/* Notch */}
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-4 bg-black rounded-b-lg" />
                        </div>

                        {/* Field of View Beam */}
                        <motion.div 
                            className="absolute top-6 -left-6 w-40 h-40 origin-top"
                            style={{ 
                                background: "linear-gradient(to bottom, rgba(255,255,255,0.15), transparent)",
                                clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)",
                                rotate: "-90deg"
                            }}
                            initial={{ opacity: 0, scaleY: 0.5 }}
                            animate={{ opacity: 1, scaleY: 1 }}
                            transition={{ 
                                duration: 2, 
                                repeat: Infinity, 
                                repeatType: "reverse", 
                                ease: "easeInOut",
                                repeatDelay: 0.5
                            }}
                        />
                    </motion.div>

                    {/* Angle Indicator */}
                    <div className="absolute top-24 left-24 pointer-events-none">
                        {/* Vertical Reference Line */}
                        <div className="absolute bottom-0 left-0 w-px h-16 bg-white/20 border-l border-dashed border-white/40" />
                        
                        {/* Arc */}
                        <svg className="absolute bottom-0 left-0 w-16 h-16 -rotate-90 overflow-visible">
                            <motion.circle
                                cx="0"
                                cy="0"
                                r="24"
                                fill="none"
                                stroke="white"
                                strokeWidth="1"
                                strokeOpacity="0.5"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 0.083 }}
                                transition={{ duration: 2, repeat: Infinity, repeatType: "reverse", ease: "easeInOut", repeatDelay: 0.5 }}
                            />
                        </svg>

                        {/* Text */}
                        <motion.div 
                            className="absolute top-4 left-8 bg-white/20 backdrop-blur-md px-1.5 py-0.5 rounded text-[10px] font-bold border border-white/30 text-white"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 2, repeat: Infinity, repeatType: "reverse", ease: "easeInOut", repeatDelay: 0.5 }}
                        >
                            30°
                        </motion.div>
                    </div>

                </div>
            </div>

            <h2 className="text-2xl font-bold mb-2 tracking-tight relative z-10">{t('ui.text.video_scan_your_food')}</h2>
            <p className="text-blue-100 text-sm max-w-[280px] leading-relaxed relative z-10">{t('ui.text.hold_the_camera_at_arm_s_length_and_slowly_pan_aro')}<span className="font-bold text-white">~3 seconds</span>. {t('ui.text.capture')} <span className="font-bold text-white">{t('ui.text.two_side_angles')}</span> {t('ui.text.and_a')} <span className="font-bold text-white">{t('ui.text.top_down_view')}</span> for best results.
            </p>
        </div>

        {/* Content Section */}
        <div className="flex-1 flex flex-col p-6 gap-6 overflow-y-auto bg-background">
            
            {/* Tip Card */}
            <div className="bg-muted/40 border rounded-xl p-4 flex gap-4 items-start">
                <div className="bg-orange-100 dark:bg-orange-900/30 p-2.5 rounded-full shrink-0 mt-0.5">
                    <Hand className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="space-y-1 min-w-0 flex-1">
                    <h3 className="font-semibold text-sm">{t('ui.text.pro_tip_use_your_finger')}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{t('ui.text.place_your_index_finger_flat_on_the_table_next_to_')}</p>
                </div>
            </div>

            {/* Size Selector */}
            <div className="space-y-3">
                <div className="space-y-1 px-1">
                    <label className="text-sm font-medium text-foreground/80">{t('ui.text.select_your_finger_size')}</label>
                    <p className="text-xs text-muted-foreground">{t('ui.text.used_to_calibrate_portion_estimation')}</p>
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                    {sizes.map((size) => (
                        <button
                            key={size.id}
                            onClick={() => setSelectedSize(size.id as any)}
                            className={cn(
                                "relative flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all duration-200 outline-none focus:ring-2 focus:ring-blue-500/20",
                                selectedSize === size.id 
                                    ? "border-blue-600 bg-blue-50/50 dark:bg-blue-900/20" 
                                    : "border-transparent bg-muted/50 hover:bg-muted hover:border-muted-foreground/20"
                            )}
                        >
                            {selectedSize === size.id && (
                                <div className="absolute top-2 right-2 text-blue-600 dark:text-blue-400">
                                    <Check className="w-3 h-3" strokeWidth={4} />
                                </div>
                            )}
                            <span className="text-2xl filter drop-shadow-sm">{size.icon}</span>
                            <div className="text-center">
                                <div className={cn(
                                    "text-xs font-semibold mb-0.5",
                                    selectedSize === size.id ? "text-blue-700 dark:text-blue-300" : "text-foreground"
                                )}>
                                    {size.label}
                                </div>
                                <div className="text-[10px] text-muted-foreground">
                                    {size.range}
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-background mt-auto space-y-3 shrink-0">
            <Button 
                className="w-full h-12 text-base font-medium shadow-lg shadow-blue-500/20 rounded-xl transition-transform active:scale-[0.98]" 
                onClick={handleProceed}
            >
                <Camera className="w-5 h-5 mr-2" />{t('ui.text.take_photo')}</Button>
            <button 
                onClick={handleDontShowAgain}
                className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors py-2"
            >{t('ui.text.don_t_show_these_tips_again')}</button>
        </div>

      </DialogContent>
    </Dialog>
  );
};
